import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Crown, MessageSquare, Eye, KeyRound, Sparkles,
  ShieldCheck, Lock, Clock, ChevronDown, Headphones, BookOpen, PlayCircle,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import BookPreviewModal from "../components/BookPreviewModal";
import GulfVideoModal from "../components/GulfVideoModal";
import GulfHeaderNav from "../components/GulfHeaderNav";
import ChatWidgetGulf from "../components/ChatWidgetGulf";
import PaymentBadges from "../components/PaymentBadges";
import TestimonialCard from "../components/TestimonialCard";
import ExitIntentPopup from "../components/ExitIntentPopup";
import Logo from "../components/Logo";
import { trackVisit, createOrder, trackCtaClick } from "../lib/api";
import { trackViewContent, trackInitiateCheckout, trackAddToCart, trackClickButton } from "../lib/tiktok";
import { snapStartCheckout } from "../lib/snapchat";
import { startSessionTracker } from "../lib/sessionTracker";
import { priceDisplay, localCurrency } from "../lib/format";
import { useGeo } from "../lib/useGeo";
import { toast } from "sonner";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/d18eb75d-910a-420b-9f07-c4ad3b278ed1/images/04bb569ebc03b0189a797318d05798b624ee2b1027e038468863044298e538b4.png";
const BOOK_MOCKUP = "/assets/hero-man.png";
const DARK_TEXTURE = "https://static.prod-images.emergentagent.com/jobs/d18eb75d-910a-420b-9f07-c4ad3b278ed1/images/af9fc0f88b4a0e9b163fbc29632f76119c5309fa5d027dffd33d7e2433633aa9.png";

const features = [
  { icon: MessageSquare, title: "بدء الحديث بثبات", text: "كيف تبدأ الحوار من الموقف نفسه، بدون جمل محفوظة أو أسئلة محرجة." },
  { icon: Eye, title: "التعامل مع الصمت", text: "كيف تتصرف عندما يتوقف الكلام، بدون أن تستعجل أو تظهر مرتبكاً." },
  { icon: KeyRound, title: "قراءة إشارات التواصل", text: "كيف تلاحظ الاهتمام، التردد، الملل، والانفتاح من خلال تفاصيل صغيرة." },
  { icon: Crown, title: "الحديث مع فتاة أعجبتك", text: "كيف تتكلم بهدوء وثقة بدون مبالغة، ضغط، أو محاولة إثبات زائدة." },
  { icon: Sparkles, title: "بناء انطباع أول أقوى", text: "كيف تظهر أكثر ثباتاً من أول لحظة، قبل أن تضطر لشرح نفسك كثيراً." },
];

const faqs = [
  { q: "هل الكتاب عن التعارف فقط؟", a: "لا. الكتاب عن الحضور وفتح الحوار وإدارة المواقف الاجتماعية، لكنه يتناول مواقف الحديث مع النساء لأنها من أكثر المواقف التي يظهر فيها التوتر." },
  { q: "هل يناسبني لو أنا شخص هادئ؟", a: "نعم. الكتاب لا يحاول تحويلك لشخص كثير الكلام. بالعكس، هو يساعدك تستخدم هدوءك بطريقة أكثر ثباتاً." },
  { q: "هل فيه جمل جاهزة؟", a: "فيه أمثلة ومداخل للحوار، لكن الهدف ليس الحفظ. الهدف أن تفهم طريقة الدخول في الكلام بدون توتر." },
  { q: "هل النسخة صوتية؟", a: "نعم. تحصل على الكتاب الرقمي مع نسخة صوتية تساعدك تراجع الأفكار بسهولة." },
  { q: "هل الشراء خاص؟", a: "نعم. الاستلام رقمي وفوري، ولا يوجد شحن أو منتج مادي." },
  { q: "هل يوجد ضمان؟", a: "نعم. إذا شعرت أن المحتوى غير مناسب لك، يمكنك طلب استرداد خلال 7 أيام." },
];

export default function Landing() {
  const navigate = useNavigate();
  const country = useGeo();
  const main = priceDisplay(27, country);
  const exitOffer = priceDisplay(22, country);
  const localUnit = localCurrency(country); // "ريال" or "درهم"
  const [previewOpen, setPreviewOpen] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showSticky, setShowSticky] = useState(false);

  useEffect(() => {
    trackVisit("landing");
    trackViewContent("pdf");
    const stop = startSessionTracker("landing");
    return stop;
  }, []);

  // Show the sticky mobile CTA only after the user scrolls past the hero CTA,
  // so the first viewport stays clean (single CTA).
  useEffect(() => {
    const onScroll = () => setShowSticky(window.scrollY > 600);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Single $27 offer (PDF + Audio bundle). All CTAs route directly to checkout.
  const openUpsell = (source) => {
    trackClickButton(source);
    trackCtaClick(source);
    trackAddToCart("pdf_audio");
    startCheckout("pdf_audio");
  };

  const startCheckout = async (pkg) => {
    setLoading(true);
    trackInitiateCheckout(pkg);
    snapStartCheckout(pkg);
    try {
      const r = await createOrder(pkg);
      navigate(`/checkout/${r.order_id}`);
    } catch (e) {
      toast.error("تعذّر بدء عملية الدفع، حاول مرة أخرى.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#040914] text-white overflow-x-hidden pb-24 md:pb-0" data-testid="landing-root">
      <ExitIntentPopup
        onClaim={() => {
          trackClickButton("exit-intent");
          trackCtaClick("exit-intent");
          trackAddToCart("pdf_audio_exit5");
          startCheckout("pdf_audio_exit5");
        }}
      />
      {/* Top Nav — full nav links replacing the single buy button.
          The big logo lives inside the Hero, so we don't repeat a small one here. */}
      <GulfHeaderNav variant="absolute" />

      {/* HERO */}
      <section className="relative pt-32 pb-16 sm:pt-40 sm:pb-24 grain-bg gold-glow">
        <div
          className="absolute inset-0 opacity-30 bg-cover bg-center"
          style={{ backgroundImage: `url('${DARK_TEXTURE}')` }}
        ></div>
        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
          <div className="flex justify-center mb-8 fade-up">
            <Logo className="h-28 w-28 sm:h-36 sm:w-36" />
          </div>
          <div className="inline-block text-[#C5A059] text-xs sm:text-sm tracking-[0.4em] mb-5 fade-up delay-100">
            دليل عملي للحضور، الثقة، وفتح الحوار
          </div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black leading-[1.25] tracking-tight fade-up delay-200">
            إذا أعجبتك بنت… <span className="text-[#C5A059]">هل تلاحظ أنك تصبح أقل طبيعية؟</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-[#A1B0C6] max-w-3xl mx-auto leading-relaxed fade-up delay-300">
            مع الناس تتكلم بسهولة. لكن أول ما يكون الانطباع مهماً…
            <br className="hidden sm:block"/>
            <span className="text-[#C5A059]">تبدأ تراجع كل كلمة، وتخاف من الصمت أكثر مما يجب.</span>
          </p>
          <p className="mt-4 text-sm sm:text-base text-white/90 max-w-3xl mx-auto leading-relaxed fade-up delay-300">
            استمع لأول سر من <span className="text-[#C5A059] font-bold">الكتاب الأزرق</span> مجاناً واكتشف لماذا يحدث ذلك.
          </p>

          <div className="mt-9 flex flex-col items-center gap-4">
            {/* Single hero CTA — opens GulfVideoModal ("first secret" teaser).
                The buy CTA + price lives INSIDE the video modal, under the player. */}
            <div className="w-full max-w-md mx-auto fade-up delay-400" data-testid="hero-cta-row">
              <Button
                data-testid="hero-video-cta"
                onClick={() => { trackClickButton("hero-video-open"); trackCtaClick("hero-video-open"); setVideoOpen(true); }}
                className="w-full bg-[#C5A059] text-[#040914] hover:bg-[#E5C07B] hover:text-[#040914] font-bold rounded-sm shadow-[0_0_40px_rgba(197,160,89,0.4)] cta-pulse h-auto px-6 py-5 sm:py-6"
              >
                <span className="flex items-center justify-center gap-2.5 leading-tight" dir="rtl">
                  <PlayCircle className="w-5 h-5 sm:w-6 sm:h-6 shrink-0" strokeWidth={2} />
                  <span className="flex flex-col items-center">
                    <span className="text-[14px] sm:text-lg">استمع إلى أول سر من الكتاب</span>
                    <span className="text-[10px] sm:text-xs font-semibold text-[#040914]/75">شغّل الفيديو الآن مجاناً</span>
                  </span>
                </span>
              </Button>
            </div>

            <div className="flex items-center justify-center gap-1.5 text-xs sm:text-sm text-[#C5A059]/90 fade-up delay-500">
              <Headphones className="w-4 h-4" />
              <span>كتاب رقمي + نسخة صوتية · استلام فوري · خصوصية كاملة · ضمان 7 أيام</span>
            </div>
            {/* Social proof — high priority, immediately under CTA */}
            <div className="text-[#C5A059] text-sm sm:text-base font-medium fade-up delay-500">
              انضم لأكثر من <span className="font-black">15,000</span> رجل قرّروا استعادة السيطرة على حياتهم الاجتماعية هذا العام.
            </div>
            {/* Trust + payment badges merged into one tight row */}
            <div className="flex flex-col items-center gap-2 fade-up delay-700">
              <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1.5 text-[11px] sm:text-xs text-[#A1B0C6]">
                <span className="flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5 text-[#C5A059]"/> ضمان الخصوصية</span>
                <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-[#C5A059]"/> تسليم فوري</span>
                <span className="flex items-center gap-1"><Lock className="w-3.5 h-3.5 text-[#C5A059]"/> دفع آمن</span>
              </div>
              <PaymentBadges emphasizeWallets className="!gap-2 opacity-90" />
              {/* Refund guarantee — risk-reversal directly under CTA */}
              <div
                className="mt-1 inline-flex items-center gap-1.5 text-[11px] sm:text-xs text-[#C5A059]"
                data-testid="refund-guarantee-hero"
              >
                <ShieldCheck className="w-3.5 h-3.5" />
                <span>ضمان استرداد خلال 7 أيام إذا لم تجد المحتوى مناسباً لك.</span>
              </div>
            </div>

            {/* Testimonial 1 — practical, not generic */}
            <div className="mt-8 w-full max-w-xl mx-auto fade-up delay-700">
              <div className="text-center text-[#C5A059] text-[10px] tracking-[0.35em] mb-3">
                من قرّاء الكتاب الأزرق
              </div>
              <TestimonialCard
                size="md"
                quote='أكثر شيء عجبني إن الكلام واقعي، مو تنظير. حسّيت إنه مكتوب لمواقف نعيشها فعلاً في الجلسات والمقابلات.'
                name="فهد"
                location="السعودية"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Pain Section */}
      <section className="relative py-20 sm:py-28 border-t border-[#C5A059]/10">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black leading-tight">
              المشكلة مو إنك ما تعرف تتكلم… <span className="text-[#C5A059]">المشكلة إنك تتوتر في اللحظة المهمة</span>
            </h2>
            <p className="mt-6 text-base sm:text-lg text-[#A1B0C6] max-w-3xl mx-auto leading-relaxed">
              في الجلسات العادية ممكن تكون طبيعي. تتكلم، تضحك، وترد بدون تفكير كثير.
              <br className="hidden sm:block"/>
              لكن أول ما يكون قدامك شخص يهمك انطباعه، كل شيء يتغير.
            </p>
            <p className="mt-4 text-sm sm:text-base text-white/90 max-w-3xl mx-auto leading-relaxed">
              تبدأ تفكر:
            </p>
          </div>
          <div className="grid gap-3 sm:gap-4 sm:grid-cols-2 max-w-4xl mx-auto">
            {[
              "أبدأ كيف؟",
              "هل كلامي بيكون ثقيل؟",
              "هل أسأل سؤال مباشر؟",
              "هل أسكت؟",
              "هل أبالغ؟",
              "هل باين عليّ التوتر؟",
              "هل هي حست إني مرتبك؟",
            ].map((p, i) => (
              <div
                key={i}
                data-testid={`pain-point-${i}`}
                className="bg-[#0B1D3A]/60 backdrop-blur-sm border-r-2 border-[#C5A059] p-4 sm:p-5 text-sm sm:text-base text-white/90 leading-relaxed"
              >
                {p}
              </div>
            ))}
          </div>
          <div className="mt-12 text-center max-w-3xl mx-auto">
            <p className="text-base sm:text-lg text-white/90 leading-relaxed">
              وهنا <span className="text-[#C5A059] font-bold">يبرد الحوار قبل ما يبدأ.</span>
            </p>
            <p className="mt-4 text-sm sm:text-base text-[#A1B0C6] leading-relaxed">
              الكتاب الأزرق معمول لهذه اللحظة تحديداً.
              لحظة البداية. لحظة الصمت. لحظة الانطباع الأول.
              اللحظة اللي إما تدخل فيها بثبات، أو تخرج منها وأنت تقول: <span className="text-white">"كان ممكن أتصرف أفضل."</span>
            </p>
          </div>

          {/* Testimonial 2 — bridges pain to solution */}
          <div className="mt-10 px-2">
            <TestimonialCard
              size="md"
              quote='كنت أحسب مشكلتي إني ما أعرف أتكلم كثير، لكن فهمت إن الحضور مو بكثرة الكلام، بل بطريقة ترتيب الكلام والهدوء.'
              name="عبدالله"
              location="الكويت"
            />
          </div>
        </div>
      </section>

      {/* Section 3 — Women / First Impression */}
      <section className="relative py-20 sm:py-28 bg-[#070f24] border-t border-[#C5A059]/10">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black leading-tight">
            الحديث مع النساء ما يحتاج تمثيل… <span className="text-[#C5A059]">يحتاج ثبات</span>
          </h2>
          <p className="mt-6 text-base sm:text-lg text-[#A1B0C6] leading-relaxed">
            كثير رجال يخطئون في أول تواصل.
          </p>
          <div className="mt-6 grid gap-3 sm:gap-4 sm:grid-cols-1 max-w-2xl mx-auto text-right" dir="rtl">
            {[
              "بعضهم يتكلم كثير عشان يغطي توتره.",
              "بعضهم يسكت لأنه خايف يقول شيء غلط.",
              "وبعضهم يحاول يكون خفيف دم زيادة، فيطلع غير طبيعي.",
            ].map((p, i) => (
              <div key={i} className="bg-[#0B1D3A]/60 border border-[#C5A059]/15 rounded-sm p-4 text-sm sm:text-base text-white/90">
                · {p}
              </div>
            ))}
          </div>
          <p className="mt-8 text-base sm:text-lg text-[#A1B0C6] leading-relaxed">
            الحضور الحقيقي ليس أنك تبهرها من أول جملة. ولا أنك تقول كلام كبير. ولا أنك تتصرف كأنك شخص مختلف.
          </p>
          <p className="mt-4 text-base sm:text-lg text-white leading-relaxed">
            الحضور الحقيقي أنك تعرف <span className="text-[#C5A059] font-bold">كيف تبدأ، كيف تسمع، كيف ترد، ومتى تصمت</span> بدون ما ينهار ثباتك.
          </p>
          <p className="mt-6 font-display text-xl sm:text-2xl font-black text-[#C5A059] leading-snug">
            الفرق أحياناً يكون في أول دقيقة فقط.
          </p>
        </div>
      </section>

      {/* Section 4 — Product Reveal */}
      <section className="relative py-20 sm:py-28 border-t border-[#C5A059]/10">
        <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="flex justify-center order-2 lg:order-1">
            <img
              src={BOOK_MOCKUP}
              alt="الحضور الهادئ — الكتاب الأزرق"
              className="w-full max-w-md rounded-lg shadow-[0_30px_60px_rgba(0,0,0,0.7),0_0_30px_rgba(197,160,89,0.18)]"
              draggable={false}
              data-testid="book-mockup-img"
            />
          </div>
          <div className="order-1 lg:order-2 text-right" dir="rtl">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black leading-tight">
              ما هو <span className="text-[#C5A059]">الكتاب الأزرق؟</span>
            </h2>
            <p className="mt-6 text-base sm:text-lg text-[#A1B0C6] leading-relaxed">
              الكتاب الأزرق هو دليل رقمي عملي للرجل الخليجي حول:
              <br/>
              <span className="text-white">فتح الحوار، تجاوز التوتر، وفهم إشارات التواصل في المواقف الاجتماعية الحساسة.</span>
            </p>
            <p className="mt-4 text-sm sm:text-base text-[#A1B0C6] leading-relaxed">
              ليس كتاباً عن الكلام الكثير. وليس عن التلاعب. وليس عن جمل محفوظة تقولها لأي بنت.
            </p>
            <p className="mt-4 text-sm sm:text-base text-white/90 leading-relaxed">
              هو كتاب يساعدك تفهم ما يحدث في اللحظات التي غالباً تربكك:
            </p>
            <ul className="mt-4 space-y-2 text-sm sm:text-base text-white/85">
              {[
                "عندما تريد بدء الحديث",
                "عندما يسكت الحوار فجأة",
                "عندما تكون أمام فتاة أعجبتك",
                "عندما لا تعرف هل الطرف الآخر مهتم أم لا",
                "عندما تريد ترك انطباع أول أقوى",
                "عندما تتكلم كثيراً بسبب التوتر",
                "عندما تصمت أكثر من اللازم خوفاً من الإحراج",
              ].map((t, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#C5A059] shrink-0"/>
                  <span>{t}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8">
              <Button
                data-testid="solution-buy-btn"
                onClick={() => openUpsell("middle")}
                className="bg-[#C5A059] text-[#040914] hover:bg-[#E5C07B] hover:text-[#040914] font-bold px-7 py-6 rounded-sm"
              >
                افتح نسختك الآن — {main.primary}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Section 5 — Core Promise */}
      <section className="relative py-20 sm:py-28 bg-[#070f24] border-t border-[#C5A059]/10">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black leading-tight">
            تعلم كيف تدخل الحوار بدون ما تبدو <span className="text-[#C5A059]">متوتر أو متصنع</span>
          </h2>
          <p className="mt-6 text-base sm:text-lg text-[#A1B0C6] leading-relaxed">
            أغلب الرجال لا يحتاجون شخصية جديدة. يحتاجون فهم أوضح للموقف.
          </p>
          <p className="mt-4 text-base text-white/90 leading-relaxed">
            لأن المشكلة غالباً ليست فيك. المشكلة أنك تدخل الحوار <span className="text-[#C5A059] font-bold">بدون خريطة.</span>
          </p>
          <div className="mt-8 grid gap-2.5 sm:gap-3 sm:grid-cols-2 max-w-2xl mx-auto text-right" dir="rtl">
            {[
              "لا تعرف متى تبدأ.",
              "لا تعرف كيف تنتقل من الصمت للكلام.",
              "لا تعرف متى تسأل ومتى تعلق.",
              "لا تعرف كيف تتعامل مع الإشارات الصغيرة.",
            ].map((t, i) => (
              <div key={i} className="bg-[#0B1D3A]/60 border border-[#C5A059]/15 rounded-sm p-3 sm:p-4 text-sm sm:text-base text-white/85">
                · {t}
              </div>
            ))}
          </div>
          <p className="mt-8 text-base sm:text-lg text-white/90 leading-relaxed">
            الكتاب الأزرق يعطيك طريقة أوضح لفهم هذه اللحظات، حتى تكون <span className="text-[#C5A059] font-bold">أهدأ، أثبت، وأكثر حضوراً.</span>
          </p>
        </div>
      </section>

      {/* Section 6 — 5 Skills */}
      <section className="relative py-20 sm:py-28 border-t border-[#C5A059]/10">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black leading-tight">
              5 مهارات يركز عليها <span className="text-[#C5A059]">الكتاب الأزرق</span>
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <div
                  key={i}
                  data-testid={`feature-card-${i}`}
                  className="group bg-[#0B1D3A]/70 backdrop-blur-sm border border-[#C5A059]/20 p-7 rounded-sm hover:border-[#C5A059]/60 transition-colors"
                >
                  <div className="w-12 h-12 mb-5 rounded-sm bg-[#C5A059]/10 border border-[#C5A059]/30 flex items-center justify-center text-[#C5A059] group-hover:bg-[#C5A059] group-hover:text-[#040914] transition-colors">
                    <Icon className="w-6 h-6" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-display text-lg sm:text-xl font-bold mb-2 leading-snug">{i + 1}. {f.title}</h3>
                  <p className="text-sm sm:text-base text-[#A1B0C6] leading-relaxed">{f.text}</p>
                </div>
              );
            })}
          </div>

          {/* Preview CTA — second placement */}
          <div className="mt-12 sm:mt-16 text-center" data-testid="features-preview-block">
            <button
              type="button"
              onClick={() => { trackClickButton("preview-features"); trackCtaClick("preview"); setPreviewOpen(true); }}
              data-testid="features-preview-link"
              className="group relative inline-flex items-center gap-3 shimmer-border rounded-full px-7 py-4 text-[#C5A059] hover:text-[#E5C07B] transition-colors text-base sm:text-lg font-bold"
            >
              <span className="absolute -top-2.5 -right-2.5 bg-[#C5A059] text-[#040914] text-[10px] font-black px-2.5 py-0.5 rounded-full shadow-md">
                مجاناً
              </span>
              <BookOpen className="w-5 h-5 group-hover:scale-110 transition-transform" />
              <span>شاهد 4 صفحات من داخل الكتاب قبل الشراء</span>
              <span className="text-[#C5A059] text-lg leading-none group-hover:translate-x-[-3px] transition-transform">←</span>
            </button>
          </div>
        </div>
      </section>

      {/* Section 7 — What You Get */}
      <section className="relative py-20 sm:py-28 bg-[#070f24] border-t border-[#C5A059]/10 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center opacity-15" style={{ backgroundImage: `url('${HERO_BG}')` }}></div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#040914]/70 via-[#040914]/85 to-[#070f24]"></div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black leading-tight">
            ماذا يصلك <span className="text-[#C5A059]">بعد الدفع مباشرة</span>؟
          </h2>
          <ul className="mt-10 grid gap-3 sm:gap-4 sm:grid-cols-2 text-right max-w-3xl mx-auto" dir="rtl">
            {[
              "الكتاب الأزرق الكامل بصيغة رقمية",
              "النسخة الصوتية للاستماع والمراجعة",
              "أمثلة عملية لفتح الحوار",
              "طرق التعامل مع الصمت والتوتر",
              "جمل ومداخل طبيعية لبداية الكلام",
              "أفكار لفهم إشارات الاهتمام والتردد",
              "محتوى مخصص لطبيعة الرجل الخليجي والمواقف اليومية",
              "ضمان استرداد خلال 7 أيام",
            ].map((t, i) => (
              <li key={i} className="flex items-center gap-3 bg-[#0B1D3A]/60 border border-[#C5A059]/15 rounded-sm p-4 text-sm sm:text-base text-white/90">
                <span className="w-5 h-5 rounded-full bg-[#C5A059] text-[#040914] flex items-center justify-center text-xs font-bold shrink-0">✓</span>
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Section 8 — Value Anchor */}
      <section className="relative py-20 sm:py-28 border-t border-[#C5A059]/10">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black leading-tight">
            <span className="text-[#C5A059]">{main.primary}</span> مقابل شيء تستخدمه<br className="hidden sm:block"/> في مواقف كثيرة
          </h2>
          <p className="mt-6 text-base sm:text-lg text-[#A1B0C6] leading-relaxed">
            الكتاب ليس مجرد قراءة لمرة واحدة. هو شيء ترجع له قبل موقف مهم:
          </p>
          <div className="mt-6 grid gap-2.5 sm:gap-3 sm:grid-cols-2 max-w-xl mx-auto text-right" dir="rtl">
            {[
              "قبل أول لقاء",
              "قبل مناسبة اجتماعية",
              "قبل جلسة فيها أشخاص جدد",
              "قبل لحظة تحتاج فيها تترك انطباع أفضل",
              "قبل موقف تخاف فيه من الصمت أو الارتباك",
            ].map((t, i) => (
              <div key={i} className="bg-[#0B1D3A]/60 border border-[#C5A059]/15 rounded-sm p-3 sm:p-4 text-sm sm:text-base text-white/90">
                · {t}
              </div>
            ))}
          </div>
          <p className="mt-8 text-base sm:text-lg text-white/90 leading-relaxed">
            تدفعها <span className="text-[#C5A059] font-bold">مرة واحدة</span>، وتحتفظ بالكتاب والنسخة الصوتية معك.
          </p>
        </div>
      </section>

      {/* Section 9 — Preview Section */}
      <section className="relative py-16 sm:py-20 bg-[#070f24] border-t border-[#C5A059]/10">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-black leading-tight">
            شاهد 4 صفحات من داخل الكتاب <span className="text-[#C5A059]">قبل الشراء</span>
          </h2>
          <p className="mt-4 text-sm sm:text-base text-[#A1B0C6] leading-relaxed">
            إذا كنت غير متأكد، شاهد عينة قصيرة من داخل الكتاب أولاً.
          </p>
          <p className="mt-3 text-sm sm:text-base text-white/90 leading-relaxed">العينة ستعطيك فكرة عن:</p>
          <div className="mt-5 grid gap-2 sm:gap-2.5 sm:grid-cols-1 max-w-md mx-auto text-right" dir="rtl">
            {[
              "أسلوب الكتاب",
              "طريقة الشرح",
              "نوع الأمثلة",
              "كيف يتعامل مع مواقف فتح الكلام والصمت والتوتر",
            ].map((t, i) => (
              <div key={i} className="text-sm sm:text-base text-white/85">· {t}</div>
            ))}
          </div>
          <Button
            data-testid="preview-cta-section"
            onClick={() => { trackClickButton("preview-section"); trackCtaClick("preview"); setPreviewOpen(true); }}
            className="mt-7 bg-transparent border-2 border-[#C5A059] text-[#C5A059] hover:bg-[#C5A059] hover:text-[#040914] font-bold text-base px-7 py-5 rounded-sm"
          >
            <BookOpen className="w-4 h-4 ml-2" />
            شاهد 4 صفحات من داخل الكتاب
          </Button>
          <div className="mt-3 text-xs text-[#A1B0C6]">العينة مجانية ولا تحتاج تسجيل</div>
        </div>
      </section>

      {/* Section 10 — Pricing */}
      <section className="relative py-20 sm:py-28 border-t border-[#C5A059]/10">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black leading-tight">
            احصل على الكتاب الأزرق<br/>
            <span className="text-[#C5A059]">الآن</span>
          </h2>

          <div className="mt-10 bg-[#0B1D3A]/80 border border-[#C5A059]/30 rounded-sm p-8 sm:p-10 shadow-[0_0_60px_rgba(197,160,89,0.12)]">
            <div className="text-[10px] sm:text-xs tracking-[0.3em] text-[#C5A059] mb-2">
              الكتاب الأزرق + الملحق الصوتي
            </div>
            <div className="flex items-baseline justify-center gap-3">
              <span className="text-xl text-white/40 line-through">219 {localUnit}</span>
              <span className="font-display text-6xl sm:text-7xl font-black text-[#C5A059]">{main.primaryNumber} <span className="text-3xl sm:text-4xl">{main.currencyShort}</span></span>
            </div>
            {main.secondary && (
              <div className="mt-1 text-sm text-[#C5A059]/90 font-semibold">{main.secondary} USD</div>
            )}
            <div className="mt-2 text-sm text-[#A1B0C6]">عرض خاص لفترة محدودة — يشمل النسخة الصوتية مجاناً</div>

            <ul className="mt-8 space-y-3 text-right max-w-md mx-auto text-white/90" dir="rtl">
              {[
                "الكتاب الرقمي الكامل",
                "النسخة الصوتية",
                "استلام فوري",
                "خصوصية كاملة",
                "ضمان 7 أيام",
              ].map((t, i) => (
                <li key={i} className="flex items-center gap-3 justify-end">
                  <span>{t}</span>
                  <span className="w-5 h-5 rounded-full bg-[#C5A059] text-[#040914] flex items-center justify-center text-xs font-bold">✓</span>
                </li>
              ))}
            </ul>

            {/* Testimonial 3 — final-decision confidence */}
            <div className="mt-8">
              <TestimonialCard
                size="md"
                quote='خلصته في نفس اليوم. أكثر جزء فادني كان أمثلة فتح الحوار والتعامل مع الصمت بدون توتر.'
                name="ناصر"
                location="الإمارات"
              />
            </div>

            <Button
              data-testid="pricing-buy-btn"
              onClick={() => openUpsell("pricing")}
              disabled={loading}
              className="mt-8 w-full bg-[#C5A059] text-[#040914] hover:bg-[#E5C07B] hover:text-[#040914] font-bold text-lg py-7 rounded-sm shadow-[0_0_30px_rgba(197,160,89,0.35)] cta-pulse"
            >
              افتح نسختك الآن — {main.primary}
            </Button>
            <div className="mt-3 text-xs text-[#A1B0C6]">
              دفع آمن · استلام فوري · بدون رسائل مزعجة
            </div>
            <div
              className="mt-2 inline-flex items-center gap-1.5 text-xs text-[#C5A059]"
              data-testid="refund-guarantee-pricing"
            >
              <ShieldCheck className="w-4 h-4" />
              <span>ضمان استرداد خلال 7 أيام إذا لم تجد المحتوى مناسباً لك.</span>
            </div>
            <PaymentBadges emphasizeWallets className="mt-5" />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative py-20 sm:py-28 border-t border-[#C5A059]/10">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="text-[#C5A059] text-xs tracking-[0.3em] mb-3">الأسئلة الشائعة</div>
            <h2 className="font-display text-3xl sm:text-4xl font-black">أسئلة قد تخطر ببالك</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
            {faqs.map((f, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="border border-[#C5A059]/20 bg-[#0B1D3A]/60 rounded-sm px-5 data-[state=open]:border-[#C5A059]/50"
              >
                <AccordionTrigger className="text-right hover:no-underline font-display font-bold text-base sm:text-lg py-5 [&>svg]:text-[#C5A059]" data-testid={`faq-q-${i}`}>
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-[#A1B0C6] text-sm sm:text-base leading-relaxed pb-5">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative border-t border-[#C5A059]/15 py-10">
        {/* About-the-author plain text block — moved to the bottom of the page
            as a credibility footer note (no border / CTA styling). */}
        <div className="max-w-3xl mx-auto px-5 sm:px-6 mb-10 sm:mb-14 text-center" data-testid="originator-credit">
          <div className="text-[#C5A059] text-[10px] sm:text-xs tracking-[0.25em] sm:tracking-[0.3em] font-bold mb-3">
            عن المؤلف
          </div>
          <p className="text-[13px] sm:text-base text-[#A1B0C6] leading-relaxed" dir="rtl">
            هذا الكتاب يرجع في الأساس للكاتب <span className="text-[#C5A059] font-bold">"جوردان بيترسون"</span> — عالم نفس وأستاذ جامعي عالمي، صاحب برامج وكتب أثّرت في حياة ملايين الرجال حول العالم، وكتبه تجاوزت مبيعاتها الـ <span className="text-[#C5A059] font-bold">10 مليون نسخة</span>. لقد درسنا أعماله بعناية، فهمنا كل أبعادها، وأعدنا صياغتها بما يتناسب مع عاداتنا وتقاليدنا.
          </p>
        </div>
        <div className="max-w-5xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-5">
          <Logo className="h-12 w-12" />
          <div className="text-xs sm:text-sm text-[#A1B0C6] text-center">
            © {new Date().getFullYear()} الكتاب الأزرق — جميع الحقوق محفوظة
          </div>
          <div className="text-xs text-[#A1B0C6] flex items-center gap-2">
            <Lock className="w-4 h-4 text-[#C5A059]"/>
            دفع آمن ومشفّر عبر Stripe
          </div>
        </div>
      </footer>

      <BookPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        onCta={() => {
          trackClickButton("preview-modal-cta");
          trackCtaClick("preview-modal-cta");
          setPreviewOpen(false);
          openUpsell("preview-modal");
        }}
      />

      {/* Hero "first secret" video modal — locked controls; buy CTA inside. */}
      <GulfVideoModal
        open={videoOpen}
        onClose={() => setVideoOpen(false)}
        onBuy={() => { setVideoOpen(false); openUpsell("video-modal"); }}
        ctaLabel="video-modal-buy"
      />

      {/* WhatsApp-style sales chatbot — always-visible floating launcher */}
      <ChatWidgetGulf />

      {/* Sticky mobile CTA — appears after the user scrolls past the hero */}
      <div
        className={`md:hidden fixed bottom-0 inset-x-0 z-40 px-3 pb-3 pt-2 bg-gradient-to-t from-[#040914] via-[#040914]/95 to-transparent transition-all duration-300 ${
          showSticky ? "translate-y-0 opacity-100 pointer-events-auto" : "translate-y-full opacity-0 pointer-events-none"
        }`}
        data-testid="sticky-cta-wrap"
      >
        <Button
          data-testid="sticky-buy-btn"
          onClick={() => openUpsell("sticky")}
          disabled={loading}
          className="w-full bg-[#C5A059] text-[#040914] hover:bg-[#E5C07B] hover:text-[#040914] font-bold text-base py-6 rounded-sm shadow-[0_0_30px_rgba(197,160,89,0.45)] cta-pulse"
        >
          افتح نسختك الآن &nbsp;·&nbsp; {main.primary}
        </Button>
      </div>
    </div>
  );
}
